<script setup lang="ts">
import { computed } from 'vue'
import Badge from '@/components/shadcn/Badge.vue'

type StyleType =
  | 'default'   // 기본 스타일
  | 'primary'   // 강조 스타일 (파랑)
  | 'success'   // 성공 스타일 (초록)
  | 'warning'   // 경고 스타일 (주황)
  | 'danger'    // 위험 스타일 (빨강)

const props = withDefaults(
  defineProps<{
    variant?: 'default' | 'secondary' | 'destructive' | 'outline'
    class?: string
    /** 스타일 프리셋 */
    styletype?: StyleType
    /** 배지 크기 */
    size?: 'xs' | 'sm' | 'md' | 'lg'
  }>(),
  {
    variant: 'default',
    styletype: 'default',
    size: 'sm',
  },
)

/**
 * styletype -> variant, class 매핑
 */
const STYLE_PRESETS: Record<StyleType, { variant?: string; class: string }> = {
  default: { class: '' },
  primary: { 
    variant: 'default',
    class: 'bg-blue-500 text-white hover:bg-blue-600',
  },
  success: { 
    variant: 'default',
    class: 'bg-green-500 text-white hover:bg-green-600',
  },
  warning: { 
    variant: 'default',
    class: 'bg-amber-500 text-white hover:bg-amber-600',
  },
  danger: { 
    variant: 'default',
    class: 'bg-red-500 text-white hover:bg-red-600',
  },
}

/**
 * size -> class 매핑
 */
const SIZE_PRESETS: Record<'xs' | 'sm' | 'md' | 'lg', string> = {
  xs: 'text-[10px] px-1 py-0.5 leading-tight',
  sm: 'text-xs px-1.5 py-0.5',
  md: 'text-sm px-2 py-1',
  lg: 'text-sm px-2.5 py-1',
}

/** 최종 바인딩: 직접 넘긴 props가 있으면 styletype 기본값과 병합 */
const mapped = computed(() => {
  const preset = STYLE_PRESETS[props.styletype!]
  const sizeClass = SIZE_PRESETS[props.size!]
  
  const finalVariant = props.variant || preset.variant
  let styleClass = ''
  
  // variant에 따른 스타일 처리
  if (finalVariant === 'outline') {
    // outline: 테두리 색상만 적용 (styletype 색상 사용)
    styleClass = getOutlineStyleClass(props.styletype!)
  } else if (finalVariant === 'secondary') {
    // secondary: shadcn CSS 변수 사용 (light/dark mode 자동 대응)
    styleClass = 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
  } else if (finalVariant === 'destructive') {
    // destructive: 고정된 빨간색 계열 스타일 (styletype 무시)
    styleClass = 'bg-red-500 text-white hover:bg-red-600'
  } else {
    // default: styletype의 배경색 스타일 적용
    styleClass = preset.class
  }
  
  const finalClass = [styleClass, sizeClass, props.class].filter(Boolean).join(' ')
  
  return {
    variant: finalVariant,
    class: finalClass,
  }
})

/**
 * outline variant용 스타일 클래스
 */
const getOutlineStyleClass = (styletype: StyleType): string => {
  const outlineStyles: Record<StyleType, string> = {
    default: 'border-border text-foreground',
    primary: 'border-blue-500 text-blue-600 dark:border-blue-400 dark:text-blue-400',
    success: 'border-green-500 text-green-600 dark:border-green-400 dark:text-green-400',
    warning: 'border-amber-500 text-amber-600 dark:border-amber-400 dark:text-amber-400',
    danger: 'border-red-500 text-red-600 dark:border-red-400 dark:text-red-400',
  }
  return outlineStyles[styletype] || ''
}
</script>

<template>
  <Badge v-bind="mapped">
    <slot />
  </Badge>
</template>
